import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  title: '',
  cover_image: '',
  body: '',
  publish_date: new Date().toISOString().slice(0, 10),
  published: true,
};

/**
 * Admin CRUD for Updates/Blog posts.
 * Parent AdminDashboard renders this inside its tab panel.
 */
const UpdatesPanel = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/updates?published_only=false`);
      setItems(res.data);
    } catch (e) {
      toast.error('Failed to load updates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      cover_image: item.cover_image,
      body: item.body,
      publish_date: item.publish_date,
      published: item.published,
    });
    setOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = editingId ? `${API}/updates/${editingId}` : `${API}/updates`;
      const method = editingId ? 'put' : 'post';
      await axios[method](url, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(editingId ? 'Update saved' : 'Update published');
      setOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this update?')) return;
    try {
      await axios.delete(`${API}/updates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Update deleted');
      fetchItems();
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <Loader2 size={32} className="animate-spin text-[#B5FF00] mx-auto" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-6">
        <button
          onClick={openCreate}
          className="btn-primary flex items-center gap-2"
          data-testid="create-update-btn"
        >
          <Plus size={18} />
          NEW UPDATE
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full" data-testid="updates-table">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">TITLE</th>
              <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">PUBLISH DATE</th>
              <th className="text-left py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">STATUS</th>
              <th className="text-right py-4 px-4 font-mono text-xs text-zinc-500 tracking-widest">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                className="border-b border-zinc-800/50 hover:bg-zinc-900/50"
                data-testid={`update-row-${item.id}`}
              >
                <td className="py-4 px-4">
                  <div className="flex items-center gap-3">
                    {item.cover_image && (
                      <img src={item.cover_image} alt="" className="w-10 h-10 object-cover" />
                    )}
                    <span className="font-['Josefin_Sans'] text-white">{item.title}</span>
                  </div>
                </td>
                <td className="py-4 px-4 font-mono text-sm text-zinc-400">{item.publish_date}</td>
                <td className="py-4 px-4">
                  <span className={`font-mono text-xs px-2 py-1 ${
                    item.published ? 'bg-[#B5FF00]/10 text-[#B5FF00]' : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    {item.published ? 'PUBLISHED' : 'DRAFT'}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => openEdit(item)}
                      className="p-2 text-zinc-400 hover:text-white"
                      data-testid={`edit-update-${item.id}`}
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 text-zinc-400 hover:text-red-500"
                      data-testid={`delete-update-${item.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && (
          <div className="text-center py-16 border border-zinc-800">
            <p className="text-zinc-500 font-mono">No updates yet. Publish your first post.</p>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Josefin_Sans'] text-2xl">
              {editingId ? 'EDIT UPDATE' : 'NEW UPDATE'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">TITLE</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                required
                data-testid="update-title-input"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">COVER IMAGE URL</label>
              <input
                type="url"
                value={form.cover_image}
                onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                placeholder="https://..."
                required
                data-testid="update-cover-input"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">BODY (markdown supported)</label>
              <textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                className="w-full h-48 bg-[#050505] border border-zinc-800 text-white px-4 py-3 focus:border-[#B5FF00] focus:outline-none resize-none"
                required
                data-testid="update-body-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-xs text-zinc-400 tracking-widest mb-2 block">PUBLISH DATE</label>
                <input
                  type="date"
                  value={form.publish_date}
                  onChange={(e) => setForm({ ...form, publish_date: e.target.value })}
                  className="w-full h-12 bg-[#050505] border border-zinc-800 text-white px-4 focus:border-[#B5FF00] focus:outline-none"
                  required
                  data-testid="update-date-input"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-3 cursor-pointer h-12">
                  <input
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) => setForm({ ...form, published: e.target.checked })}
                    className="w-5 h-5"
                    data-testid="update-published-input"
                  />
                  <span className="font-mono text-sm text-zinc-400">Published</span>
                </label>
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary flex-1">
                CANCEL
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="save-update-btn"
              >
                {saving ? <><Loader2 size={18} className="animate-spin" />SAVING...</> : 'SAVE'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UpdatesPanel;
